import * as React from 'react';

import { Button, cn, humanReadableFileSize } from '@rematter/ui-web';
import {
  AlertCircle,
  CheckCircle,
  Download,
  FileText,
  FileX,
  Image,
  Loader2,
  Trash2,
} from 'lucide-react';

import { FileFormData, GenericFile } from '~/modules/trade/types/file';

type DefaultFileCardProps = {
  file: FileFormData;
  onDownload: () => void;
  onDelete: () => void;
  onRetry: () => void;
  onFileClick?: (file: GenericFile) => void;
  isDeleting: boolean;
  isDownloading: boolean;
  isUploading: boolean;
  retryLabel: string;
  addedOnLabel: string;
};

const getFileIcon = (mimeType: string) => {
  if (mimeType === 'application/pdf') {
    return <FileText className="size-6 text-failure opacity-50" />;
  } else if (mimeType.startsWith('image/')) {
    return <Image className="size-6 text-primary opacity-50" />;
  } else if (mimeType.startsWith('text/')) {
    return <FileText className="size-6 text-neutral opacity-50" />;
  } else {
    return <FileX className="size-6 text-neutral opacity-50" />;
  }
};

const uploadStateIcon: Record<string, React.ReactNode> = {
  uploading: (
    <Loader2 className="size-4 animate-spin text-primary opacity-50" />
  ),
  uploaded: <CheckCircle className="size-4 text-success opacity-50" />,
  confirmed: <CheckCircle className="size-4 text-success opacity-50" />,
  error: <AlertCircle className="size-4 text-failure opacity-50" />,
};

export const DefaultFileCard: React.FC<DefaultFileCardProps> = ({
  file,
  onDownload,
  onDelete,
  onRetry,
  onFileClick,
  isDeleting,
  isDownloading,
  isUploading,
  retryLabel,
  addedOnLabel,
}) => {
  const actions = (
    <div className="flex items-center gap-2">
      {file.uploadState === 'error' && file.file && (
        <Button
          variant="outline"
          size="small"
          onClick={onRetry}
          className="text-xs"
          isDisabled={isUploading}
        >
          {retryLabel}
        </Button>
      )}

      {file.status === 'Active' && (
        <Button
          variant="outline"
          size="small"
          onClick={onDownload}
          className="p-2 text-primary/60 hover:bg-primary/5"
          isDisabled={isDeleting || isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
      )}
      <Button
        variant="outline"
        size="small"
        onClick={onDelete}
        className="p-2 text-failure/60 hover:bg-failure/5"
        isDisabled={isDeleting || file.uploadState === 'uploading'}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );

  const handleFileClick = () => {
    if (onFileClick && file.status === 'Active') {
      onFileClick({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        mimeType: file.mimeType,
        status: file.status,
        downloadUrl: file.downloadUrl,
        entityType: file.metadata?.entityType,
        entityId: file.metadata?.entityId,
        metadata: file.metadata,
        createdAt: file.metadata?.createdAt,
        updatedAt: file.metadata?.updatedAt,
      });
    }
  };

  return (
    <div className="@DefaultFileCard flex items-center gap-4 rounded-lg border border-neutral/20 bg-neutral/5 p-3">
      <div className="flex-shrink-0">{getFileIcon(file.mimeType)}</div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <p
            className={cn(
              'truncate text-sm font-medium text-neutral/90',
              onFileClick && file.status === 'Active'
                ? 'cursor-pointer hover:text-primary/60'
                : '',
            )}
            onClick={handleFileClick}
          >
            {file.fileName}
          </p>
          {file.mimeType === 'application/pdf' && (
            <span className="inline-flex items-center rounded bg-failure/10 px-2 py-0.5 text-xs font-medium text-failure/80">
              PDF
            </span>
          )}
          {file.mimeType.startsWith('text/') && (
            <span className="inline-flex items-center rounded bg-neutral/10 px-2 py-0.5 text-xs font-medium text-neutral/80">
              TXT
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-neutral/50">
            {(file.file || file.fileSize) &&
              `${humanReadableFileSize(file.file?.size || file.fileSize || 0)} • `}
            {addedOnLabel}{' '}
            {file.metadata?.createdAt &&
              new Intl.DateTimeFormat(navigator.language, {
                dateStyle: 'medium',
              }).format(new Date(file.metadata.createdAt))}
          </p>

          {!['uploaded', 'confirmed'].includes(file.uploadState) &&
            uploadStateIcon[file.uploadState]}

          {['uploaded', 'confirmed'].includes(file.uploadState) && (
            <CheckCircle className="size-4 text-success opacity-50" />
          )}
        </div>

        {file.uploadState === 'uploading' &&
          file.uploadProgress !== undefined && (
            <div className="mt-1 h-1.5 w-full rounded-full bg-neutral/20">
              <div
                className="h-1.5 rounded-full bg-primary/60 transition-all duration-300"
                style={{ width: `${file.uploadProgress}%` }}
              />
            </div>
          )}
        {file.error && (
          <p className="mt-1 text-xs text-failure/50">{file.error}</p>
        )}
      </div>

      {actions}
    </div>
  );
};
